// src/oura/components/docs/DeleteDocDialog.tsx
//
// Destructive-confirm for deleting one file from the client's folder (#4631,
// backend ourteam-backend#6451). Same shell and order as DeleteBusinessModal:
// red icon, the question as the title, the loss named first, then the typed
// confirmation that turns the button on.
//
// The file goes to Google Drive's trash, not away for good: it can be restored
// there for 30 days, and Oura files it again on the next folder read. The word
// to type is the file's own NAME, exactly (spaces round it do not count): docs
// of one client differ only by version or date, so typing it is also the check
// that this is the right one. The server checks it again.
//
// A refusal keeps the dialog open with the server's sentence: oura@ can only
// trash a file it owns, and the sentence names the owner who can.
//
// Drawn in a portal on <body>: the row it opens from is a size container
// (`container-type: inline-size`), which holds `position: fixed` inside it, so
// rendered in place the dialog would be cut to the row.

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { AlertTriangleIcon, Loader2Icon, Trash2Icon } from "lucide-react";
import { Input } from "@component/input";
import { trashDocument, type StaffDocument } from "@service/aeo/clientDocs";
import { Button } from "../../primitives/Button";

/** Typed against the file name: the letters and their case, not the spaces round them. */
const same = (typed: string, name: string) => typed.trim() === name.trim();

export function DeleteDocDialog({ doc, open, onClose }: { doc: StaffDocument; open: boolean; onClose: () => void }) {
    // What was typed belongs to this doc: another doc, or the dialog reopened, starts empty.
    const [confirmation, setConfirmation] = useState(() => ({ docId: doc.id, typed: "" }));
    const [deleting, setDeleting] = useState(false);
    const [failed, setFailed] = useState<string | null>(null);
    useEffect(() => {
        setConfirmation({ docId: doc.id, typed: "" });
        setFailed(null);
    }, [open, doc.id]);
    const typed = confirmation.docId === doc.id ? confirmation.typed : "";
    const confirmed = doc.name.trim() !== "" && same(typed, doc.name);

    if (!open) return null;

    const confirm = async () => {
        if (!confirmed || deleting) return;
        setDeleting(true);
        setFailed(null);
        const r = await trashDocument(doc.id, typed.trim());
        setDeleting(false);
        // On success the board re-reads (thenEmit) and this row leaves it.
        if (r.ok) onClose();
        else setFailed(r.message);
    };
    const cancel = () => !deleting && onClose();
    const titleId = `delete-doc-title-${doc.id}`;
    const fieldId = `delete-doc-confirm-${doc.id}`;

    return createPortal(
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            data-delete-doc-dialog={doc.id}
            // A portal still bubbles through React to the row's parents: keep clicks here.
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === "Escape") cancel();
                if (e.key === "Enter" && (e.target as HTMLElement).tagName === "INPUT") void confirm();
            }}
        >
            {/* No dismiss on the backdrop while the request is in flight. */}
            <div className="absolute inset-0 bg-gray-900/50" onClick={cancel} />

            <div className="relative w-full max-w-md rounded-lg bg-white shadow-xl">
                <div className="flex items-start gap-3 px-6 pt-5">
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-rose-100">
                        <AlertTriangleIcon className="h-5 w-5 text-rose-600" />
                    </div>
                    <div className="min-w-0 pt-1.5">
                        <h2 id={titleId} className="text-base font-semibold text-gray-900">
                            Delete this file?
                        </h2>
                    </div>
                </div>

                <div className="space-y-3 px-6 pb-1 pt-3">
                    <p className="break-words text-sm leading-relaxed text-gray-900">
                        <span className="font-medium">{doc.name}</span> moves to the Google Drive trash and leaves
                        this board, with its questions and comments in Oura.
                    </p>
                    {doc.sharing.clientCanSee && (
                        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5">
                            <p className="text-sm font-medium text-amber-900">The client can open this file today.</p>
                            <p className="mt-1 text-xs leading-relaxed text-amber-800">
                                Once it is deleted, their link stops working.
                            </p>
                        </div>
                    )}
                    <p className="text-sm leading-relaxed text-gray-600">
                        It can be restored from the trash in Google Drive for 30 days; Oura shows it again on the
                        next folder read.
                    </p>
                </div>

                {/* Typing the name is what turns the delete button on. Focus starts here,
                    never on the destructive button, and Enter confirms only once it matches. */}
                <div className="space-y-2 px-6 pt-3">
                    <label htmlFor={fieldId} className="block text-sm font-medium text-gray-900">
                        Type the file name to confirm
                    </label>
                    <p className="m-0 select-all break-all rounded-md bg-gray-100 px-2.5 py-1.5 font-mono text-xs text-gray-800">
                        {doc.name}
                    </p>
                    <Input
                        id={fieldId}
                        autoFocus
                        autoComplete="off"
                        spellCheck={false}
                        value={typed}
                        disabled={deleting}
                        onChange={(e) => setConfirmation({ docId: doc.id, typed: e.target.value })}
                        placeholder="File name"
                        aria-describedby={failed ? `${fieldId}-error` : undefined}
                    />
                    {failed && (
                        <p id={`${fieldId}-error`} role="alert" className="m-0 text-sm text-rose-600">
                            {failed}
                        </p>
                    )}
                </div>

                <div className="flex gap-2 px-6 pb-5 pt-4">
                    <Button variant="outline" className="flex-1" disabled={deleting} onClick={cancel}>
                        Cancel
                    </Button>
                    <Button
                        variant="destructive"
                        className="flex-1"
                        disabled={deleting || !confirmed}
                        onClick={() => void confirm()}
                        data-delete-doc-confirm={doc.id}
                    >
                        {deleting ? (
                            <>
                                <Loader2Icon className="h-4 w-4 animate-spin" />
                                Deleting…
                            </>
                        ) : (
                            <>
                                <Trash2Icon className="h-4 w-4" />
                                Delete file
                            </>
                        )}
                    </Button>
                </div>
            </div>
        </div>,
        document.body,
    );
}
